import { useCallback, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { getVisitFrequencyCustomers } from "../../services";
import {
  NO_ROUTE_PARAM,
  VisitBucketKey,
  VisitFrequencyCustomer,
  VisitFrequencyCustomerParams,
  VisitFrequencyMode,
  VisitFrequencyRoute,
} from "../../types/visit-frequency.type";
import { formatThaiShortDate } from "../../utils/date";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { downloadBlob } from "../SalesReportExport/utils";
import CsvExportButton from "./CsvExportButton";
import {
  BUCKETS_BY_MODE,
  BUCKET_LABEL,
  bucketLabel,
  buildCsv,
  csvBlob,
  formatCount,
  routeLabel,
} from "./visitFrequency";

/** UI value for "no filter" in the two selects; never sent to the API. */
export const ALL_OPTION = "all";

/** The export asks for every row in one call — the server's page-size ceiling. */
export const EXPORT_LIMIT = 5000;

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

interface CustomerTableProps {
  dateFrom: string;
  dateTo: string;
  holidays: string[];
  branchCode: string;
  branchLabel: string;
  mode: VisitFrequencyMode;
  routes: VisitFrequencyRoute[];
  /** `ALL_OPTION`, `NO_ROUTE_PARAM`, or a route_number. */
  route: string;
  onRouteChange: (route: string) => void;
  /** `ALL_OPTION` or a bucket key. */
  bucket: string;
  onBucketChange: (bucket: string) => void;
}

const routeText = (row: VisitFrequencyCustomer) =>
  row.route_code ?? row.route_number ?? "ไม่มีสาย";

/** Level 3 — the shops behind a count, with the visit/bill day counts that placed them. */
export default function CustomerTable({
  dateFrom,
  dateTo,
  holidays,
  branchCode,
  branchLabel,
  mode,
  routes,
  route,
  onRouteChange,
  bucket,
  onBucketChange,
}: Readonly<CustomerTableProps>) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [rows, setRows] = useState<VisitFrequencyCustomer[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);

  const holidayKey = holidays.join(",");

  const baseParams = useCallback((): VisitFrequencyCustomerParams => {
    const params: VisitFrequencyCustomerParams = {
      date_from: dateFrom,
      date_to: dateTo,
      holidays: holidayKey ? holidayKey.split(",") : [],
      branch_code: branchCode,
    };
    if (route !== ALL_OPTION) params.route_number = route;
    if (bucket !== ALL_OPTION) params.bucket = bucket as VisitBucketKey;
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [dateFrom, dateTo, holidayKey, branchCode, route, bucket, debouncedSearch]);

  // Any filter change redefines the list, so land back on its first page.
  useEffect(() => {
    setPage(0);
  }, [baseParams]);

  // A filter change reloads twice (once as it lands, once when the page resets); only the
  // latest request may write, or a slow first answer would overwrite the right one.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const response = await getVisitFrequencyCustomers({
        ...baseParams(),
        page: page + 1,
        limit,
      });
      if (id !== requestId.current) return;
      setRows(response.data.data?.rows ?? []);
      setCount(response.data.data?.count ?? 0);
    } catch {
      if (id !== requestId.current) return;
      setError(true);
      setRows([]);
      setCount(0);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [baseParams, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExportNote(null);
    const response = await getVisitFrequencyCustomers({
      ...baseParams(),
      page: 1,
      limit: EXPORT_LIMIT,
    });
    const data = response.data.data;
    const all = data?.rows ?? [];
    if ((data?.count ?? 0) > all.length) {
      setExportNote(
        `ไฟล์มี ${formatCount(all.length)} จาก ${formatCount(data?.count)} ร้าน — กรองตามสายเพื่อให้ได้ครบ`,
      );
    }
    const csv = buildCsv([
      ["สาขา", branchLabel],
      ["ช่วงวันที่", `${dateFrom} ถึง ${dateTo}`],
      [],
      [
        "รหัสร้าน",
        "ชื่อร้าน",
        "สาย",
        "วันที่เข้า",
        "วันที่มีบิล",
        "เข้าแต่ขายไม่ได้ (วัน)",
        "รอบ",
        "วันที่ขายล่าสุด",
      ],
      ...all.map((row) => [
        row.customer_code,
        row.customer_name,
        routeText(row),
        row.visit_days,
        row.bill_days,
        row.visited_not_sold_days,
        bucketLabel(row.bucket),
        row.last_bill_date ?? "",
      ]),
    ]);
    downloadBlob(
      `visit-frequency_${branchCode}_shops_${dateFrom}_${dateTo}.csv`,
      csvBlob(csv),
    );
  };

  const bucketOptions = BUCKETS_BY_MODE[mode];

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            รายชื่อร้าน — {branchLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            เรียงจากร้านที่เข้าน้อยที่สุดก่อน
          </Typography>
        </Box>
        <CsvExportButton onExport={handleExport} disabled={loading || count === 0} />
      </Box>

      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สาย"
            value={route}
            onChange={(event) => onRouteChange(event.target.value)}
          >
            <MenuItem value={ALL_OPTION}>ทั้งหมด</MenuItem>
            {routes.map((r) => (
              <MenuItem key={r.route_number ?? NO_ROUTE_PARAM} value={r.route_number ?? NO_ROUTE_PARAM}>
                {routeLabel(r)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="รอบ"
            value={bucket}
            onChange={(event) => onBucketChange(event.target.value)}
          >
            <MenuItem value={ALL_OPTION}>ทั้งหมด</MenuItem>
            {bucketOptions.map((b) => (
              <MenuItem key={b} value={b}>
                {BUCKET_LABEL[b]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            size="small"
            fullWidth
            label="ค้นหา"
            placeholder="รหัสร้าน หรือ ชื่อร้าน"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>

      {exportNote && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setExportNote(null)}>
          {exportNote}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          โหลดรายชื่อร้านไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
        </Alert>
      )}

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead sx={{ "& th": { bgcolor: "#F8F9FC", fontWeight: 600 } }}>
            <TableRow>
              <TableCell>รหัสร้าน</TableCell>
              <TableCell>ชื่อร้าน</TableCell>
              <TableCell>สาย</TableCell>
              <TableCell align="right">วันที่เข้า</TableCell>
              <TableCell align="right">วันที่มีบิล</TableCell>
              <TableCell align="right">เข้าแต่ขายไม่ได้ (วัน)</TableCell>
              <TableCell>รอบ</TableCell>
              <TableCell>วันที่ขายล่าสุด</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={3}>
                    <CircularProgress size={24} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    ไม่พบร้านตามเงื่อนไขที่เลือก
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => (
                <TableRow hover key={row.customer_uuid}>
                  <TableCell>{row.customer_code}</TableCell>
                  <TableCell sx={{ whiteSpace: "normal !important", minWidth: 200 }}>
                    {row.customer_name}
                  </TableCell>
                  <TableCell>{routeText(row)}</TableCell>
                  <TableCell align="right">{formatCount(row.visit_days)}</TableCell>
                  <TableCell align="right">{formatCount(row.bill_days)}</TableCell>
                  <TableCell align="right">{formatCount(row.visited_not_sold_days)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={bucketLabel(row.bucket)}
                      color={row.bucket === "none" ? "warning" : "default"}
                      variant={row.bucket === "none" ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    {row.last_bill_date
                      ? formatThaiShortDate(row.last_bill_date, { weekday: false })
                      : "ไม่เคยมีบิล"}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={limit}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={(event) => {
          setLimit(Number(event.target.value));
          setPage(0);
        }}
        labelRowsPerPage="แถวต่อหน้า"
        labelDisplayedRows={({ from, to, count: total }) => `${from}-${to} จาก ${total}`}
      />
    </Paper>
  );
}
